import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFeedbackContext } from '../../../context/FeedbackContext';
import feedbacksConfiguration from './feedbacksConfiguration';
import CloseIcon from '../CloseIcon';

/**
 * FeedbackModal Component
 *
 * This component displays a global feedback modal for critical messages (e.g., success, warning, or error),
 * intended to capture the user's full attention. It reads its configuration (visibility, variant, title, copy,
 * etc.) from the global FeedbackContext.
 *
 * - If `isVisible` (in the context state) is `true`, the modal is rendered with an overlay.
 * - Pressing the Escape key or clicking the close icon/button will trigger `hideModal()`.
 * - A primary action button (if provided) executes `onClickButton` and then also closes the modal.
 *
 * Accessibility:
 *  - Uses `role="dialog"` and `aria-modal="true"` to ensure screen readers interpret it as a modal dialog.
 *  - `aria-labelledby` and `aria-describedby` provide context for assistive technologies.
 */
function FeedbackModal() {
  const { state, hideModal } = useFeedbackContext();
  const { isVisible, variant, title, copy, buttonMessage, onClickButton } = state.modal;

  const { icon: Icon, color } = feedbacksConfiguration[variant] || {};
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        hideModal();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, hideModal]);

  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    hideModal();
  };

  const handleButtonClick = () => {
    if (typeof onClickButton === 'function') {
      onClickButton();
    }
    hideModal();
  };

  const feedbackModalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      aria-describedby="feedback-modal-description"
    >
      <div className="relative bg-white shadow-md rounded-xl max-w-[335px] flex flex-col items-center p-[31px] pb-[60px]">
        <div className="bg-white p-4 rounded w-full">
          <button
            ref={closeButtonRef}
            className="absolute p-[12px] top-[31px] right-[31px] text-gray-500 bg-[#F2F4F8] rounded-full"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <CloseIcon size="11" />
          </button>
          <div className="flex flex-col items-center mt-[54px]">
            <div className="w-[119px] h-[119px] bg-[#F2F6FA] rounded-full flex items-center justify-center mb-[30px]">
              {Icon && <Icon />}
            </div>
            <div className="items-center justify-center">
              <h2
                id="feedback-modal-title"
                className="mt-4 mb-[30px] text-[22px] text-center font-semibold text-gray-800 leading-[28px]"
              >
                {title}
              </h2>
              <p
                id="feedback-modal-description"
                className="mt-2 mb-[45px] text-center text-gray-600"
              >
                {copy}
              </p>
            </div>
            <button
              onClick={handleButtonClick}
              className={`${color} mt-6 text-white font-medium py-2 px-4 w-full rounded-[10px] max-w-[203px]`}
            >
              {buttonMessage}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(feedbackModalContent, document.getElementById('feedback-portal-root'));
}

export default FeedbackModal;
